/**
 * PipelineProgressCard — Stripe-inspired horizontal pipeline tracker.
 * Authority: docs/skills/DESIGN_LANGUAGE_SKILL.md + WI-PROGRESS-CARD-V3 brief.
 * Tokens: DS_COLORS, DS_SPACING, DS_BORDERS, DS_ANIMATION.
 *
 * Visual contract:
 *   - Horizontal row of 48px circles connected by a 2px rail.
 *   - Waiting: light border + muted inner dot.
 *   - Active:  solid navy fill + white inner dot + breathing navy ring pulse.
 *   - Done:    white fill + navy border + SVG checkmark drawn via pathLength.
 *   - Failed:  white fill + red border + SVG X drawn via pathLength.
 *   - Rail fill: gradient (navy → navy-accent) scaleX 0→1 behind completed segments.
 *   - On full completion, checkmarks cascade left → right via staggered delay.
 *
 * Props:
 *   steps          — [{ key, label }] ordered pipeline phases
 *   currentPhase   — string key of the active step
 *   state          — 'not_started' | 'running' | 'complete' | 'failed' | 'partial'
 *   progressDetail — optional subtitle shown only on the active step
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DS_COLORS,
  DS_SPACING,
  DS_BORDERS,
  DS_ANIMATION,
  DS_TYPOGRAPHY,
} from '@/config/ui-invariants';

const CIRCLE_SIZE = 48;
const INNER_DOT = 8;
const RAIL_HEIGHT = 2;
const RAIL_OFFSET = (CIRCLE_SIZE - RAIL_HEIGHT) / 2;
const RING_OUTSET = 6;
const STROKE_WIDTH = 2.5;
const SVG_SIZE = 22;
const LABEL_WIDTH = 180;
const LABEL_GUTTER = (LABEL_WIDTH - CIRCLE_SIZE) / 2;
const LABEL_GAP = 16;

function getStatus(idx, activeIdx, state) {
  if (state === 'complete' || state === 'partial') return 'done';
  if (state === 'failed') {
    if (activeIdx < 0) return idx === 0 ? 'failed' : 'waiting';
    if (idx < activeIdx) return 'done';
    if (idx === activeIdx) return 'failed';
    return 'waiting';
  }
  if (activeIdx < 0) return 'waiting';
  if (idx < activeIdx) return 'done';
  if (idx === activeIdx) return 'active';
  return 'waiting';
}

function Checkmark({ delay, color }) {
  return (
    <svg width={SVG_SIZE} height={SVG_SIZE} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <motion.path
        d="M5.5 12.5 L10 17 L18.5 7.5"
        stroke={color}
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{
          pathLength: { duration: DS_ANIMATION.checkmarkDrawDuration, delay, ease: DS_ANIMATION.checkmarkEase },
          opacity: { duration: DS_ANIMATION.stepTransitionDuration, delay },
        }}
      />
    </svg>
  );
}

function Cross({ color }) {
  return (
    <svg width={SVG_SIZE} height={SVG_SIZE} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <motion.path
        d="M7 7 L17 17"
        stroke={color}
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: DS_ANIMATION.checkmarkDrawDuration, ease: DS_ANIMATION.checkmarkEase }}
      />
      <motion.path
        d="M17 7 L7 17"
        stroke={color}
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{
          duration: DS_ANIMATION.checkmarkDrawDuration,
          delay: DS_ANIMATION.checkmarkDrawDuration * 0.4,
          ease: DS_ANIMATION.checkmarkEase,
        }}
      />
    </svg>
  );
}

function StepCircle({ status, cascadeDelay }) {
  const shell = {
    width: CIRCLE_SIZE,
    height: CIRCLE_SIZE,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  };

  if (status === 'active') {
    return (
      <div style={{ position: 'relative', width: CIRCLE_SIZE, height: CIRCLE_SIZE }}>
        <motion.span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: -RING_OUTSET,
            borderRadius: '50%',
            border: `2px solid ${DS_COLORS.text.brand}`,
            pointerEvents: 'none',
          }}
          initial={{ scale: 1, opacity: DS_ANIMATION.ringPulseOpacity }}
          animate={{
            scale: DS_ANIMATION.ringPulseScale,
            opacity: [DS_ANIMATION.ringPulseOpacity, 0, DS_ANIMATION.ringPulseOpacity],
          }}
          transition={{
            duration: DS_ANIMATION.ringPulseDuration,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          initial={{ scale: 0.9, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: DS_ANIMATION.stepTransitionDuration, ease: DS_ANIMATION.checkmarkEase }}
          style={{
            ...shell,
            background: DS_COLORS.text.brand,
            border: `2px solid ${DS_COLORS.text.brand}`,
          }}
        >
          <span style={{
            width: INNER_DOT,
            height: INNER_DOT,
            borderRadius: '50%',
            background: DS_COLORS.surface.white,
          }} />
        </motion.div>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <motion.div
        initial={{ scale: 0.88, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          duration: DS_ANIMATION.stepTransitionDuration,
          delay: cascadeDelay,
          ease: DS_ANIMATION.checkmarkEase,
        }}
        style={{
          ...shell,
          background: DS_COLORS.surface.white,
          border: `2px solid ${DS_COLORS.text.brand}`,
        }}
      >
        <Checkmark delay={cascadeDelay} color={DS_COLORS.text.brand} />
      </motion.div>
    );
  }

  if (status === 'failed') {
    return (
      <motion.div
        initial={{ scale: 0.88, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: DS_ANIMATION.stepTransitionDuration, ease: DS_ANIMATION.checkmarkEase }}
        style={{
          ...shell,
          background: DS_COLORS.surface.white,
          border: `2px solid ${DS_COLORS.text.error}`,
        }}
      >
        <Cross color={DS_COLORS.text.error} />
      </motion.div>
    );
  }

  return (
    <div style={{
      ...shell,
      background: DS_COLORS.surface.white,
      border: `1.5px solid ${DS_COLORS.border.default}`,
    }}>
      <span style={{
        width: INNER_DOT,
        height: INNER_DOT,
        borderRadius: '50%',
        background: DS_COLORS.text.muted,
        opacity: 0.45,
      }} />
    </div>
  );
}

function ConnectorRail({ filled, delay }) {
  return (
    <div style={{
      flex: 1,
      height: RAIL_HEIGHT,
      marginTop: RAIL_OFFSET,
      background: DS_COLORS.border.default,
      position: 'relative',
      overflow: 'hidden',
      borderRadius: RAIL_HEIGHT,
    }}>
      <motion.div
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(to right, ${DS_COLORS.gradient.brandStart}, ${DS_COLORS.gradient.brandEnd})`,
          transformOrigin: 'left center',
          borderRadius: RAIL_HEIGHT,
        }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: filled ? 1 : 0 }}
        transition={{
          duration: DS_ANIMATION.connectorFillDuration,
          delay: filled ? delay : 0,
          ease: DS_ANIMATION.checkmarkEase,
        }}
      />
    </div>
  );
}

function StepLabel({ status, label, progressDetail }) {
  const lit = status !== 'waiting';
  return (
    <div style={{
      marginTop: LABEL_GAP,
      marginLeft: -LABEL_GUTTER,
      marginRight: -LABEL_GUTTER,
      width: LABEL_WIDTH,
      textAlign: 'center',
      pointerEvents: 'none',
    }}>
      {/* Step label: minimum 16px per CEO a11y rule (≥16px body text).
          WI-DS-PROGRESS-VISIBILITY-BUILD-001 Change C: bumped from 13px → 16px.
          Justification: CLAUDE.md §13 "minimum 16px body"; DESIGN_LANGUAGE_SKILL.md
          body text is Inter 16px 400; step labels are short descriptive text (not
          meta/timestamp), so 16px is correct per the design system's body scale.
          Token used: DS_TYPOGRAPHY.bodyMd (16px Inter 400). */}
      <motion.p
        initial={false}
        animate={{
          color: lit ? DS_COLORS.text.primary : DS_COLORS.text.muted,
          fontWeight: lit ? 600 : 400,
        }}
        transition={{ duration: DS_ANIMATION.stepTransitionDuration }}
        style={{
          fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
          fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
          letterSpacing: '-0.005em',
          lineHeight: 1.35,
          margin: 0,
        }}
      >
        {label}
      </motion.p>
      <AnimatePresence initial={false}>
        {status === 'active' && progressDetail && (
          <motion.p
            key="sub"
            initial={{ opacity: 0, maxHeight: 0, marginTop: 0 }}
            animate={{ opacity: 1, maxHeight: 60, marginTop: 4 }}
            exit={{ opacity: 0, maxHeight: 0, marginTop: 0 }}
            transition={{ duration: DS_ANIMATION.stepTransitionDuration, ease: 'easeOut' }}
            style={{
              /* Sub-detail: minimum 16px per CEO a11y rule. Old: 11px.
                 WI-DS-PROGRESS-VISIBILITY-BUILD-001 Change C: bumped 11→16px.
                 Justification: same as step label above — 11px is below any
                 sanctioned body size in the design system and violates the
                 CLAUDE.md ≥16px accessibility floor. Token: DS_TYPOGRAPHY.bodyMd. */
              fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
              fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
              lineHeight: DS_TYPOGRAPHY.bodyMd.lineHeight,
              color: DS_COLORS.text.secondary,
              margin: 0,
              overflow: 'hidden',
            }}
          >
            {progressDetail}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

export function PipelineProgressCard({ steps = [], currentPhase, state = 'not_started', progressDetail }) {
  if (steps.length === 0) return null;

  const activeIdx = steps.findIndex((s) => s.key === currentPhase);
  const cascading = state === 'complete' || state === 'partial';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: DS_ANIMATION.checkmarkEase }}
      style={{
        background: DS_COLORS.surface.white,
        border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
        borderRadius: DS_BORDERS.radiusCard,
        boxShadow: DS_COLORS.shadow.card,
        padding: `${DS_SPACING.cardPadding + 4}px ${DS_SPACING.cardPadding + 8}px ${DS_SPACING.cardPadding + 8}px`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {steps.map((step, i) => {
          const status = getStatus(i, activeIdx, state);
          const cascadeDelay = cascading ? i * DS_ANIMATION.checkmarkStaggerDelay : 0;
          const railFilled = status === 'done';
          const railDelay = cascading ? i * DS_ANIMATION.checkmarkStaggerDelay : 0;

          return (
            <React.Fragment key={step.key}>
              <div style={{
                width: CIRCLE_SIZE,
                flexShrink: 0,
                position: 'relative',
                zIndex: 1,
              }}>
                <StepCircle status={status} cascadeDelay={cascadeDelay} />
                <StepLabel status={status} label={step.label} progressDetail={progressDetail} />
              </div>
              {i < steps.length - 1 && (
                <ConnectorRail filled={railFilled} delay={railDelay} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </motion.div>
  );
}

export default PipelineProgressCard;
